import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useResourceRates, useResourceStats } from './hooks/useResourceRates';
import { Resource, ResourceRate } from './types';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { TopbarSearch } from '@/components/ui';

// --- MOCK DATA ---
const KPI_DATA = [
  { label: 'Tổng bộ định mức', count: '142', avg: 'Hạng mục', var: '+12', icon: 'ri-book-read-line', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { label: 'Vật tư', count: '1,250', avg: 'Vật liệu & Cây xanh', var: '+45', icon: 'ri-apps-2-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { label: 'Nhân công', count: '24', avg: 'Nhóm nghề/bậc', var: '+2', icon: 'ri-user-settings-line', color: 'text-blue-600', bg: 'bg-blue-50' },
  { label: 'Máy móc', count: '36', avg: 'Loại thiết bị', var: '0', icon: 'ri-truck-line', color: 'text-orange-600', bg: 'bg-orange-50' },
  { label: 'Công tác áp dụng', count: '5,240', avg: 'BOQ items', var: '+128', icon: 'ri-task-line', color: 'text-purple-600', bg: 'bg-purple-50' },
  { label: 'Định mức lỗi', count: '3', avg: 'Thiếu dữ liệu', var: '-2', icon: 'ri-error-warning-line', color: 'text-rose-600', bg: 'bg-rose-50' },
  { label: 'Phiên bản hiện tại', count: 'Rev. 14', avg: 'Cập nhật 07/05/26', var: '-', icon: 'ri-history-line', color: 'text-slate-600', bg: 'bg-slate-50' },
  { label: 'Trạng thái', count: 'Active', avg: 'Đang áp dụng', var: '-', icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
];

const TABS = [
  { id: 'overview', label: 'Tổng quan', icon: 'ri-dashboard-2-line' },
  { id: 'standard_tasks', label: 'Công tác chuẩn', icon: 'ri-task-line' },
  { id: 'materials', label: 'Vật tư', icon: 'ri-apps-2-line' },
  { id: 'labor', label: 'Nhân công', icon: 'ri-user-settings-line' },
  { id: 'equipment', label: 'Máy móc', icon: 'ri-truck-line' },
  { id: 'agro', label: 'Phân thuốc', icon: 'ri-flask-line' },
  { id: 'norm_sets', label: 'Bộ định mức', icon: 'ri-book-read-line' },
  { id: 'formula', label: 'Công thức', icon: 'ri-functions' },
  { id: 'waste', label: 'Hao hụt', icon: 'ri-delete-bin-2-line' },
  { id: 'reference_cost', label: 'Giá tham chiếu', icon: 'ri-money-dollar-circle-line' },
  { id: 'revisions', label: 'Phiên bản', icon: 'ri-history-line' },
  { id: 'ai', label: 'AI Phân tích', icon: 'ri-robot-2-line' },
  { id: 'import_export', label: 'Import/Export', icon: 'ri-file-excel-2-line' },
];

// 1. Tổng quan
const MOCK_OVERVIEW = [
  { id: 1, group: 'Vật liệu', count: 128, avgValue: '185.000', variance: '+4.5%', status: 'Đang dùng' },
  { id: 2, group: 'Cây xanh', count: 256, avgValue: '420.000', variance: '+8.2%', status: 'Cần kiểm tra' },
  { id: 3, group: 'Nhân công', count: 24, avgValue: '380.000/ngày', variance: '+3.0%', status: 'Ổn định' },
  { id: 4, group: 'Máy móc', count: 36, avgValue: '1.250.000/ca', variance: '+5.8%', status: 'Đang dùng' },
  { id: 5, group: 'Nhiên liệu', count: 8, avgValue: '23.500/lít', variance: '+2.1%', status: 'Đang dùng' },
  { id: 6, group: 'Phân thuốc', count: 64, avgValue: '145.000', variance: '+6.4%', status: 'Cần duyệt' },
  { id: 7, group: 'Nhà cung cấp', count: 42, avgValue: '-', variance: '-', status: 'Hoạt động' },
];

// 2. Vật liệu
const MOCK_MATERIALS = [
  { code: 'VL-001', name: 'Đất trồng hữu cơ', group: 'Đất & giá thể', unit: 'm3', price: '320.000', waste: '5%', vat: '8%', supplier: 'Vườn Xanh Phan Thiết', status: 'Đang dùng' },
  { code: 'VL-002', name: 'Đất sạch san nền', group: 'Đất & giá thể', unit: 'm3', price: '180.000', waste: '3%', vat: '8%', supplier: 'Bình Thuận Soil', status: 'Đang dùng' },
  { code: 'VL-003', name: 'Cát vàng', group: 'Vật liệu nền', unit: 'm3', price: '280.000', waste: '4%', vat: '8%', supplier: 'VLXD Minh Phát', status: 'Đang dùng' },
  { code: 'VL-004', name: 'Đá mi bụi', group: 'Vật liệu nền', unit: 'm3', price: '310.000', waste: '3%', vat: '8%', supplier: 'VLXD Minh Phát', status: 'Đang dùng' },
  { code: 'VL-005', name: 'Cỏ lá gừng', group: 'Cỏ', unit: 'm2', price: '38.000', waste: '7%', vat: '8%', supplier: 'Vườn Cỏ Miền Nam', status: 'Đang dùng' },
  { code: 'VL-006', name: 'Cỏ nhung Nhật', group: 'Cỏ', unit: 'm2', price: '62.000', waste: '8%', vat: '8%', supplier: 'Vườn Cỏ Miền Nam', status: 'Theo dõi' },
  { code: 'VL-007', name: 'Ống HDPE D32', group: 'Tưới tự động', unit: 'm', price: '22.000', waste: '2%', vat: '8%', supplier: 'Irritech VN', status: 'Đang dùng' },
  { code: 'VL-008', name: 'Béc tưới pop-up', group: 'Tưới tự động', unit: 'cái', price: '145.000', waste: '1%', vat: '8%', supplier: 'Irritech VN', status: 'Đang dùng' },
  { code: 'VL-009', name: 'Đèn bollard sân vườn', group: 'Đèn cảnh quan', unit: 'cái', price: '680.000', waste: '1%', vat: '10%', supplier: 'Light Garden VN', status: 'Đang dùng' },
  { code: 'VL-010', name: 'Dây điện CVV 2x2.5', group: 'Đèn cảnh quan', unit: 'm', price: '19.500', waste: '2%', vat: '10%', supplier: 'Điện Nước An Phát', status: 'Đang dùng' },
];

// 3. Cây xanh
const MOCK_PLANTS = [
  { code: 'CX-001', name: 'Osaka đỏ', size: 'D6', height: '3.0m', canopy: 'D6', unit: 'cây', import: '650.000', trans: '80.000', waste: '8%', est: '790.000', status: 'Đang dùng' },
  { code: 'CX-002', name: 'Sao đen', size: 'D10', height: '4.5m', canopy: 'D10', unit: 'cây', import: '1.850.000', trans: '220.000', waste: '10%', est: '2.280.000', status: 'Đang dùng' },
  { code: 'CX-003', name: 'Lộc vừng', size: 'D12', height: '4.0m', canopy: 'D12', unit: 'cây', import: '2.400.000', trans: '300.000', waste: '10%', est: '2.970.000', status: 'Theo dõi' },
  { code: 'CX-004', name: 'Cau vua', size: 'H4m', height: '4.0m', canopy: 'Bầu 70cm', unit: 'cây', import: '1.250.000', trans: '180.000', waste: '7%', est: '1.530.000', status: 'Đang dùng' },
  { code: 'CX-005', name: 'Hoa giấy leo', size: 'H2.5m', height: '2.5m', canopy: 'Bầu 45cm', unit: 'cây', import: '280.000', trans: '45.000', waste: '12%', est: '370.000', status: 'Đang dùng' },
  { code: 'CX-006', name: 'Chuỗi ngọc', size: 'Bụi', height: '0.4m', canopy: 'Bầu nhỏ', unit: 'cây', import: '18.000', trans: '2.000', waste: '10%', est: '24.000', status: 'Đang dùng' },
  { code: 'CX-007', name: 'Dâm bụt thái', size: 'Bụi', height: '0.6m', canopy: 'Bầu nhỏ', unit: 'cây', import: '35.000', trans: '4.000', waste: '8%', est: '46.000', status: 'Đang dùng' },
  { code: 'CX-008', name: 'Trầu bà vàng', size: 'Groundcover', height: '0.25m', canopy: 'Khay', unit: 'm2', import: '42.000', trans: '5.000', waste: '10%', est: '55.000', status: 'Đang dùng' },
];

// 4. Nhân công
const MOCK_LABOR = [
  { code: 'NC-001', name: 'Công nhân phổ thông', group: 'Thi công', unit: 'ngày', rate: '350.000', ot: '55.000/giờ', night: '70.000/giờ', skill: 'Cơ bản', status: 'Đang dùng' },
  { code: 'NC-002', name: 'Thợ trồng cây', group: 'Cây xanh', unit: 'ngày', rate: '420.000', ot: '65.000/giờ', night: '80.000/giờ', skill: 'Khá', status: 'Đang dùng' },
  { code: 'NC-003', name: 'Thợ tưới tự động', group: 'Irrigation', unit: 'ngày', rate: '550.000', ot: '85.000/giờ', night: '105.000/giờ', skill: 'Cao', status: 'Đang dùng' },
  { code: 'NC-004', name: 'Thợ điện cảnh quan', group: 'Lighting', unit: 'ngày', rate: '580.000', ot: '90.000/giờ', night: '110.000/giờ', skill: 'Cao', status: 'Đang dùng' },
  { code: 'NC-005', name: 'Tổ trưởng', group: 'Quản lý đội', unit: 'ngày', rate: '650.000', ot: '100.000/giờ', night: '125.000/giờ', skill: 'Cao', status: 'Đang dùng' },
  { code: 'NC-006', name: 'Kỹ sư cảnh quan', group: 'Giám sát', unit: 'ngày', rate: '900.000', ot: '140.000/giờ', night: '170.000/giờ', skill: 'Cao', status: 'Đang dùng' },
  { code: 'NC-007', name: 'QA/QC hiện trường', group: 'Kiểm soát chất lượng', unit: 'ngày', rate: '850.000', ot: '130.000/giờ', night: '160.000/giờ', skill: 'Cao', status: 'Đang dùng' },
];

// 5. Máy móc
const MOCK_EQUIP = [
  { code: 'MM-001', name: 'Máy đào mini', group: 'Cơ giới', unit: 'ca', rate: '1.800.000', fuel: '350.000', op: '500.000', note: 'Đào hố cây, san nền nhỏ' },
  { code: 'MM-002', name: 'Xe cẩu 5 tấn', group: 'Vận chuyển/cẩu', unit: 'ca', rate: '2.800.000', fuel: '650.000', op: '600.000', note: 'Cẩu cây lớn' },
  { code: 'MM-003', name: 'Xe tải 2.5 tấn', group: 'Vận chuyển', unit: 'chuyến', rate: '1.200.000', fuel: '300.000', op: '400.000', note: 'Chở cây/vật tư' },
  { code: 'MM-004', name: 'Máy cắt cỏ', group: 'Bảo dưỡng', unit: 'ngày', rate: '350.000', fuel: '80.000', op: '350.000', note: 'Cắt cỏ khu lớn' },
  { code: 'MM-005', name: 'Máy xới đất', group: 'Cải tạo đất', unit: 'ngày', rate: '450.000', fuel: '100.000', op: '350.000', note: 'Xới nền trồng' },
  { code: 'MM-006', name: 'Máy phun thuốc', group: 'Phân thuốc', unit: 'ngày', rate: '300.000', fuel: '60.000', op: '350.000', note: 'Phun thuốc BVTV' },
  { code: 'MM-007', name: 'Máy đầm cóc', group: 'Hardscape', unit: 'ngày', rate: '420.000', fuel: '90.000', op: '350.000', note: 'Đầm nền lát đá' },
];

// 6. Nhiên liệu
const MOCK_FUEL = [
  { code: 'NL-001', name: 'Dầu Diesel', unit: 'lít', price: '21.800', var: '+2.4%', source: 'Giá thị trường', date: '07/05/2026', status: 'Đang dùng' },
  { code: 'NL-002', name: 'Xăng RON 95', unit: 'lít', price: '24.500', var: '+1.8%', source: 'Giá thị trường', date: '07/05/2026', status: 'Đang dùng' },
  { code: 'NL-003', name: 'Xăng E5', unit: 'lít', price: '23.100', var: '+1.5%', source: 'Giá thị trường', date: '07/05/2026', status: 'Đang dùng' },
  { code: 'NL-004', name: 'Nhớt máy', unit: 'lít', price: '95.000', var: '+0.5%', source: 'NCC thiết bị', date: '07/05/2026', status: 'Đang dùng' },
  { code: 'NL-005', name: 'Gas công nghiệp', unit: 'kg', price: '32.000', var: '+3.2%', source: 'NCC ngoài', date: '07/05/2026', status: 'Theo dõi' },
];

// 7. Phân thuốc
const MOCK_AGRO = [
  { code: 'PT-001', name: 'NPK 16-16-8', group: 'Phân bón', unit: 'kg', price: '18.000', dose: '30g/m2', cost: '540/m2', status: 'Đang dùng' },
  { code: 'PT-002', name: 'Humic', group: 'Cải tạo đất', unit: 'kg', price: '65.000', dose: '10g/m2', cost: '650/m2', status: 'Đang dùng' },
  { code: 'PT-003', name: 'Vitamin B1', group: 'Chống sốc cây', unit: 'lít', price: '120.000', dose: '5ml/cây', cost: '600/cây', status: 'Đang dùng' },
  { code: 'PT-004', name: 'Kích rễ', group: 'Dưỡng cây', unit: 'lít', price: '180.000', dose: '5ml/cây', cost: '900/cây', status: 'Đang dùng' },
  { code: 'PT-005', name: 'Thuốc nấm gốc đồng', group: 'BVTV', unit: 'kg', price: '145.000', dose: '2g/lít nước', cost: '290/lít', status: 'Đang dùng' },
  { code: 'PT-006', name: 'Thuốc sâu sinh học', group: 'BVTV', unit: 'lít', price: '210.000', dose: '3ml/lít nước', cost: '630/lít', status: 'Theo dõi' },
  { code: 'PT-007', name: 'Phân vi sinh', group: 'Cải tạo đất', unit: 'kg', price: '28.000', dose: '50g/m2', cost: '1.400/m2', status: 'Đang dùng' },
];

// 8. Nhà cung cấp
const MOCK_SUPPLIER = [
  { code: 'NCC-001', name: 'Vườn Xanh Phan Thiết', group: 'Cây xanh', area: 'Bình Thuận', rating: '4.6/5', lead: '2 ngày', debt: '30 ngày', status: 'Hoạt động' },
  { code: 'NCC-002', name: 'Vườn Cỏ Miền Nam', group: 'Cỏ', area: 'TP.HCM', rating: '4.4/5', lead: '3 ngày', debt: '15 ngày', status: 'Hoạt động' },
  { code: 'NCC-003', name: 'Irritech VN', group: 'Tưới tự động', area: 'TP.HCM', rating: '4.8/5', lead: '5 ngày', debt: '30 ngày', status: 'Hoạt động' },
  { code: 'NCC-004', name: 'Light Garden VN', group: 'Đèn cảnh quan', area: 'Đồng Nai', rating: '4.5/5', lead: '7 ngày', debt: '30 ngày', status: 'Hoạt động' },
  { code: 'NCC-005', name: 'VLXD Minh Phát', group: 'Vật liệu nền', area: 'Bình Thuận', rating: '4.2/5', lead: '1 ngày', debt: '15 ngày', status: 'Hoạt động' },
  { code: 'NCC-006', name: 'AgroCare VN', group: 'Phân thuốc', area: 'Long An', rating: '4.3/5', lead: '4 ngày', debt: '15 ngày', status: 'Theo dõi' },
];

// 9. Lịch sử giá
const MOCK_HISTORY = [
  { code: 'LS-001', name: 'Osaka đỏ D6', old: '720.000', new: '790.000', diff: '+9.7%', by: 'QS', reason: 'Giá vườn tăng', date: '07/05/2026' },
  { code: 'LS-002', name: 'Cỏ lá gừng', old: '35.000', new: '38.000', diff: '+8.6%', by: 'Procurement', reason: 'Nhu cầu tăng', date: '06/05/2026' },
  { code: 'LS-003', name: 'Công nhân phổ thông', old: '330.000', new: '350.000', diff: '+6.1%', by: 'Admin', reason: 'Điều chỉnh lương vùng', date: '05/05/2026' },
  { code: 'LS-004', name: 'Máy đào mini', old: '1.650.000', new: '1.800.000', diff: '+9.1%', by: 'Equipment Manager', reason: 'Tăng nhiên liệu', date: '04/05/2026' },
  { code: 'LS-005', name: 'Ống HDPE D32', old: '23.000', new: '22.000', diff: '-4.3%', by: 'Procurement', reason: 'Đổi NCC', date: '03/05/2026' },
  { code: 'LS-006', name: 'NPK 16-16-8', old: '17.000', new: '18.000', diff: '+5.9%', by: 'Agro', reason: 'Giá nhập tăng', date: '02/05/2026' },
];

// 10. Import / Export
const MOCK_FILES = [
  { code: 'IMP-001', name: 'bang-gia-cay-xanh-05-2026.xlsx', type: 'Cây xanh', rows: 256, success: 248, err: 8, by: 'QS', status: 'Cần kiểm tra' },
  { code: 'IMP-002', name: 'don-gia-vat-lieu.xlsx', type: 'Vật liệu', rows: 128, success: 128, err: 0, by: 'Procurement', status: 'Hoàn tất' },
  { code: 'IMP-003', name: 'don-gia-nhan-cong.xlsx', type: 'Nhân công', rows: 24, success: 24, err: 0, by: 'Admin', status: 'Hoàn tất' },
  { code: 'IMP-004', name: 'bang-gia-may-moc.xlsx', type: 'Máy móc', rows: 36, success: 34, err: 2, by: 'Equipment Manager', status: 'Cần kiểm tra' },
  { code: 'EXP-001', name: 'export-master-cost-2026.xlsx', type: 'Tất cả', rows: 516, success: 516, err: 0, by: 'Director', status: 'Đã xuất' },
];

// 11. AI Pricing
const MOCK_AI = [
  { code: 'AI-001', group: 'Cây xanh', desc: 'Giá Osaka đỏ D6 tăng 9.7% trong 7 ngày', level: 'Cao', suggest: 'So sánh thêm 3 NCC', status: 'Chưa xử lý' },
  { code: 'AI-002', group: 'Nhân công', desc: 'Đơn giá công nhân thấp hơn mặt bằng khu vực 6%', level: 'Trung bình', suggest: 'Cập nhật benchmark', status: 'Đang xem' },
  { code: 'AI-003', group: 'Máy móc', desc: 'Chi phí máy đào mini tăng do nhiên liệu', level: 'Trung bình', suggest: 'Tách riêng fuel surcharge', status: 'Chưa xử lý' },
  { code: 'AI-004', group: 'Cỏ', desc: 'Giá cỏ nhung Nhật cao hơn cỏ lá gừng 63%', level: 'Thấp', suggest: 'Gợi ý thay thế theo ngân sách', status: 'Đã xem' },
  { code: 'AI-005', group: 'Phân thuốc', desc: 'Chi phí kích rễ cao ở nhóm cây lớn', level: 'Trung bình', suggest: 'Tối ưu liều lượng theo size cây', status: 'Chưa xử lý' },
  { code: 'AI-006', group: 'NCC', desc: 'NCC Irritech VN có giá cao nhưng lead time ổn định', level: 'Thấp', suggest: 'Dùng cho dự án cần tiến độ nhanh', status: 'Đã xem' },
];

// 12. Công tác chuẩn
const MOCK_STANDARD_TASKS = [
  { code: 'CT-CX-001', name: 'Trồng cây bóng mát D15-20', unit: 'Cây', group: 'Cây xanh', comp: '5 Vật tư, 2 Nhân công, 1 Máy', time: '1.5h/cây', status: 'Active' },
  { code: 'CT-CX-002', name: 'Trồng cỏ nhung Nhật', unit: 'm2', group: 'Cây xanh', comp: '3 Vật tư, 1 Nhân công', time: '0.1h/m2', status: 'Active' },
  { code: 'CT-TD-001', name: 'Thi công tưới nhỏ giọt', unit: 'm', group: 'Tưới tự động', comp: '4 Vật tư, 1 Nhân công', time: '0.2h/m', status: 'Active' },
  { code: 'CT-BD-001', name: 'Cắt tỉa cây xanh <5m', unit: 'Cây', group: 'Bảo dưỡng', comp: '1 Nhân công, 1 Máy', time: '2.0h/cây', status: 'Draft' },
];

// 13. Bộ định mức
const MOCK_NORM_SETS = [
  { code: 'NS-2026-HCM', name: 'Bộ định mức Thi công HCM 2026', type: 'Construction', region: 'TP.HCM', items: 450, updated: '01/05/2026', status: 'Active' },
  { code: 'NS-2026-HN', name: 'Bộ định mức Thi công HN 2026', type: 'Construction', region: 'Hà Nội', items: 450, updated: '01/05/2026', status: 'Active' },
  { code: 'NS-MAINT-01', name: 'Định mức Bảo dưỡng Vinpearl', type: 'Maintenance', region: 'Toàn quốc', items: 120, updated: '15/04/2026', status: 'Active' },
];

// 14. Hao hụt
const MOCK_WASTE_RULES = [
  { group: 'Cây bóng mát D>10', rule: 'Hao hụt vận chuyển', value: '2%', alert: 'Bình thường' },
  { group: 'Cây bóng mát D>10', rule: 'Tỷ lệ chết (3 tháng)', value: '5%', alert: 'Cao (TB: 3%)' },
  { group: 'Cỏ thảm', rule: 'Hao hụt thi công', value: '8%', alert: 'Bình thường' },
  { group: 'Ống tưới PVC', rule: 'Hao hụt cắt nối', value: '3%', alert: 'Bình thường' },
];

// 15. Công thức
const MOCK_FORMULAS = [
  { name: 'Đơn giá Tổng hợp (ĐGTH)', formula: 'VL + NC + CM + HaoHụt(VL)', type: 'Hệ thống', status: 'Active' },
  { name: 'Chi phí Quản lý (Overhead)', formula: 'ĐGTH * 5%', type: 'Hệ thống', status: 'Active' },
  { name: 'Giá Bán Dự kiến (Sales Price)', formula: '(ĐGTH + Overhead) * (1 + ProfitMargin)', type: 'Custom', status: 'Active' },
];

// 16. Giá tham chiếu
const MOCK_REF_COSTS = [
  { code: 'VL-005', name: 'Cỏ lá gừng', current: '38.000', refA: 'Vườn A: 37.500', refB: 'Vườn B: 39.000', history: 'Trung bình 2025: 35.000' },
  { code: 'CX-001', name: 'Osaka đỏ D6', current: '790.000', refA: 'Nam Điền: 780.000', refB: 'Sa Đéc: 810.000', history: 'Dự án X: 750.000' },
];

// 17. Phiên bản
const MOCK_REVISIONS = [
  { rev: 'Rev. 14', date: '07/05/2026', changes: 'Cập nhật giá vật tư Q2', author: 'QS Lead', status: 'Current' },
  { rev: 'Rev. 13', date: '01/01/2026', changes: 'Ban hành bảng giá 2026', author: 'System', status: 'Archived' },
  { rev: 'Rev. 12', date: '15/10/2025', changes: 'Bổ sung định mức tưới', author: 'Technical Dept', status: 'Archived' },
];

export function ResourceRatesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TABS[2].id); // Mặc định Vật tư
  const [historyView, setHistoryView] = useState<'table' | 'timeline'>('table');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Backend Integration
  const tabToType: Record<string, any> = {
    'materials': 'MATERIAL',
    'plants': 'AGRO',
    'labor': 'LABOR',
    'equipment': 'EQUIPMENT'
  };

  const { resources, isLoading, updateRate } = useResourceRates(tabToType[activeTab]);
  const { data: stats } = useResourceStats();
  const [selectedItem, setSelectedItem] = useState<any>(null);

  useEffect(() => {
    if (resources.length > 0 && !selectedItem) {
      setSelectedItem(resources[0]);
    }
  }, [resources]);

  const toggleRowSelection = (code: string) => {
    setSelectedRows(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const toggleAllRows = (codes: string[]) => {
    setSelectedRows(prev => prev.length === codes.length ? [] : codes);
  };

  const Sparkline = ({ colorClass }: { colorClass: string }) => (
    <svg className={`absolute bottom-0 left-0 w-full h-12 opacity-[0.05] ${colorClass}`} viewBox="0 0 100 30" preserveAspectRatio="none">
      <path d="M0,30 L0,15 Q10,25 20,10 T40,20 T60,5 T80,15 T100,0 L100,30 Z" fill="currentColor" />
    </svg>
  );

  const MicroTrend = ({ up }: { up: boolean }) => (
    <svg className={`w-10 h-4 ${up ? 'text-rose-500' : 'text-emerald-500'} drop-shadow-sm`} viewBox="0 0 50 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      {up ? <path d="M5 15 L15 10 L25 12 L45 5" /> : <path d="M5 5 L15 10 L25 8 L45 15" />}
    </svg>
  );

  const RowActions = () => (
    <td className="p-2">
      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); navigate(`/estimates/rates/form?type=${activeTab}&mode=edit`); }} className="w-7 h-7 flex items-center justify-center rounded bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-emerald-600 shadow-sm transition-colors" title="Xem chi tiết"><i className="ri-eye-line"></i></button>
        <button onClick={(e) => { e.stopPropagation(); navigate(`/estimates/rates/form?type=${activeTab}&mode=edit`); }} className="w-7 h-7 flex items-center justify-center rounded bg-white border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 shadow-sm transition-colors" title="Sửa"><i className="ri-pencil-line"></i></button>
        <button onClick={(e) => { e.stopPropagation(); toast.success('Đã xóa dữ liệu thành công'); }} className="w-7 h-7 flex items-center justify-center rounded bg-white border border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 shadow-sm transition-colors" title="Xóa"><i className="ri-delete-bin-line"></i></button>
      </div>
    </td>
  );

  
  // Helper cho badge
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'Đang dùng': case 'Hoạt động': case 'Hoàn tất': case 'Đã xuất':
        return 'success';
      case 'Theo dõi': case 'Đang xem':
        return 'info';
      case 'Cần kiểm tra': case 'Chưa xử lý':
        return 'warning';
      case 'Cần duyệt':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getVarColor = (val: string) => {
    if (val.startsWith('+')) return 'text-rose-600';
    if (val.startsWith('-')) return 'text-emerald-600';
    return 'text-slate-600';
  };

  const getLevelColor = (level: string) => {
    if (level === 'Cao') return 'border-rose-200 bg-rose-50 text-rose-700';
    if (level === 'Trung bình') return 'border-amber-200 bg-amber-50 text-amber-700';
    return 'border-slate-200 bg-slate-50 text-slate-600';
  };

  const TableActions = () => (
    <div className="p-3 border-b border-slate-100 bg-white flex justify-between items-center shrink-0 sticky top-0 z-20">
      <div className="flex gap-2">
        <TopbarSearch value="" onChange={() => {}} onSubmit={() => {}} placeholder={`Tìm ${activeTab}...`} className="w-64" />
        <button onClick={() => setIsModalOpen(true)} className="h-10 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-md flex items-center gap-2 transition-colors shadow-sm">
          <i className="ri-file-upload-line text-lg"></i> Import
        </button>
      </div>
      <div className="flex gap-2">
        <button className="h-10 px-4 bg-white border border-slate-200 text-slate-600 text-sm rounded-md flex items-center gap-2 hover:bg-slate-50 transition-colors shadow-sm">
          <i className="ri-filter-3-line"></i> Bộ lọc
        </button>
        <button onClick={() => navigate(`/estimates/rates/form?type=${activeTab}&mode=new`)} className="h-10 px-4 bg-emerald-700 hover:bg-emerald-800 text-white text-sm rounded-md flex items-center gap-2 transition-colors shadow-sm">
          <i className="ri-add-line text-lg"></i> Thêm mới
        </button>
      </div>
    </div>
  );

  const IntelligenceSidebar = ({ item }: { item: any }) => {
    if (!item) return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-white border border-slate-200 rounded-xl">
        <i className="ri-information-line text-4xl text-slate-200 mb-2"></i>
        <p className="text-sm text-slate-400">Chọn một item để xem phân tích thông minh</p>
      </div>
    );

    return (
      <div className="flex flex-col gap-4 h-full animate-in fade-in slide-in-from-right-4 duration-300">
        <Card className="p-4 border-slate-200 shadow-sm bg-white shrink-0">
          <div className="flex justify-between items-start mb-3">
            <Badge variant="info">{item.code || 'ITEM-ID'}</Badge>
            <Badge variant={getBadgeVariant(item.status)}>{item.status}</Badge>
          </div>
          <h4 className="text-base font-normal tracking-tight text-slate-900 mb-1">{item.name}</h4>
          <p className="text-xs text-slate-500 mb-4">{item.category?.name || item.group || 'Chưa phân nhóm'}</p>
          
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
            <div>
              <p className="text-[10px] tracking-widest tracking-wider text-slate-400 mb-1 font-normal">Giá hiện tại</p>
              <p className="text-lg font-normal text-slate-900">{(item.current_rate?.price || 0).toLocaleString()} <span className="text-xs text-slate-400">đ</span></p>
            </div>
            <div>
              <p className="text-[10px] tracking-widest tracking-wider text-slate-400 mb-1 font-normal">Biến động (30d)</p>
              <p className="text-lg font-normal text-rose-600 flex items-center gap-1">+4.2% <i className="ri-arrow-up-line text-sm"></i></p>
            </div>
          </div>
        </Card>

        {/* AI Benchmarking */}
        <Card className="p-4 border-purple-100 shadow-sm bg-purple-50/30 flex flex-col gap-3 shrink-0">
          <h5 className="text-xs font-normal tracking-wide text-purple-800 flex items-center gap-2">
            <i className="ri-robot-2-line"></i> AI Price Benchmark
          </h5>
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-white p-2 rounded border border-purple-100">
              <span className="text-xs text-slate-500 font-normal">Giá thị trường (TB)</span>
              <span className="text-xs font-normal text-slate-700">{(Number(item.current_rate?.price || 0) * 0.95).toLocaleString()} đ</span>
            </div>
            <div className="p-2 bg-emerald-50 border border-emerald-100 rounded">
              <p className="text-[10px] text-emerald-700 font-normal mb-1">KIẾM NGHỊ AI</p>
              <p className="text-xs text-emerald-800 font-normal">Đơn giá đang cao hơn thị trường 5%. Đề xuất đàm phán lại với NCC {item.current_rate?.supplier?.name || 'chính'}.</p>
            </div>
          </div>
        </Card>

        {/* Supplier Options - Real data or history */}
        <Card className="p-4 border-slate-200 shadow-sm bg-white flex-1 overflow-auto">
          <h5 className="text-xs font-normal tracking-wide text-slate-800 mb-3 flex items-center gap-2">
            <i className="ri-truck-line text-emerald-600"></i> Lịch sử / NCC khác
          </h5>
          <div className="space-y-2">
            {item.rates?.map((r: any, i: number) => (
              <div key={i} className="p-2 border border-slate-100 rounded-lg hover:border-emerald-200 transition-all group">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-normal text-slate-800 group-hover:text-emerald-700">{r.supplier?.name || 'NCC Hệ thống'}</span>
                  <span className="text-xs font-normal text-slate-900">{r.price?.toLocaleString()} đ</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-[10px] text-slate-400 font-normal">Ngày: {new Date(r.effective_from).toLocaleDateString()}</span>
                  <Badge variant={getBadgeVariant(r.status)} className="scale-75 origin-left">{r.status}</Badge>
                </div>
              </div>
            )) || (
               <p className="text-xs text-slate-400 text-center py-4">Chưa có lịch sử biến động</p>
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 relative overflow-hidden">
      {/* Floating Bulk Action Bar */}
      {selectedRows.length > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-10 border border-slate-700">
          <span className="font-medium border-r border-slate-700 pr-6">{selectedRows.length} mục đã chọn</span>
          <button onClick={() => toast.info('Chức năng đổi giá hàng loạt đang tải...')} className="text-sm font-medium hover:text-emerald-400 transition-colors flex items-center"><i className="ri-edit-circle-line mr-1 text-lg"></i>Đổi giá</button>
          <button onClick={() => toast.info('Đang mở form chọn NCC...')} className="text-sm font-medium hover:text-blue-400 transition-colors flex items-center"><i className="ri-truck-line mr-1 text-lg"></i>Đổi NCC</button>
          <button onClick={() => { setSelectedRows([]); toast.success('Đã xóa các mục đã chọn'); }} className="text-sm font-medium hover:text-rose-400 transition-colors flex items-center"><i className="ri-delete-bin-line mr-1 text-lg"></i>Xóa</button>
          <button onClick={() => setSelectedRows([])} className="ml-4 p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-full transition-colors flex items-center"><i className="ri-close-line text-xl"></i></button>
        </div>
      )}

      <div className="p-4 flex-shrink-0 z-10 bg-white border-b border-slate-200 backdrop-blur-md bg-white/90 sticky top-0">
        <PageHeaderNew
          title="Master Data: Định giá Nguồn lực (Pricing Engine)"
          subtitle="Quản lý tập trung toàn bộ Đơn giá Vật tư, Cây xanh, Nhân công, Máy móc toàn hệ thống"
        />
        
        {/* Header Stats Bar - Polished */}
        <div className="flex gap-8 mt-4 text-xs font-normal text-slate-600 bg-slate-50/50 p-2.5 rounded-xl border border-slate-100 shadow-inner overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-2 shrink-0 border-r border-slate-200 pr-8">
            <i className="ri-database-2-line text-slate-400"></i>
            <span className="text-slate-400 tracking-widest tracking-widest text-[9px]">Tổng items:</span> 
            <span className="font-normal text-slate-800">{stats?.totalItems?.toLocaleString() || '...'}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 border-r border-slate-200 pr-8">
            <i className="ri-refresh-line text-emerald-500"></i>
            <span className="text-slate-400 tracking-widest tracking-widest text-[9px]">Cập nhật hôm nay:</span> 
            <span className="font-normal text-emerald-700">0 mục</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 border-r border-slate-200 pr-8">
            <i className="ri-truck-line text-blue-500"></i>
            <span className="text-slate-400 tracking-widest tracking-widest text-[9px]">NCC active:</span> 
            <span className="font-normal text-blue-700">{stats?.totalSuppliers || '...'} đối tác</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 border-r border-slate-200 pr-8">
            <i className="ri-alert-line text-amber-500 animate-pulse"></i>
            <span className="text-slate-400 tracking-widest tracking-widest text-[9px]">Pending Approval:</span> 
            <span className="font-normal text-amber-700">5 yêu cầu</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <i className="ri-robot-2-line text-purple-500"></i>
            <span className="text-slate-400 tracking-widest tracking-widest text-[9px]">AI Price Insight:</span> 
            <span className="font-normal text-purple-700">12 phát hiện</span>
          </div>
        </div>

        {/* Scrollable Tabs - Standardized */}
        <div className="mt-4 flex overflow-x-auto no-scrollbar border-b border-slate-100">
          <div className="flex space-x-1 min-w-max pb-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-xs font-normal tracking-wide rounded-t-lg transition-all flex items-center gap-2 border-b-2
                  ${activeTab === tab.id ? 
                    (tab.id === 'ai' ? 'bg-purple-50 text-purple-700 border-purple-500' : 'bg-emerald-50/50 text-emerald-700 border-emerald-600') 
                    : 'text-slate-500 hover:bg-slate-50 border-transparent hover:text-slate-700'
                  }`}
              >
                <i className={`${tab.icon} ${activeTab === tab.id ? 'animate-bounce-slow' : ''}`} /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {/* 1. OVERVIEW TAB - INTELLIGENCE BENTO */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-min animate-in fade-in zoom-in-95 duration-500">
            
            {/* Bento 1: AI Market Opportunity (Large) */}
            <Card className="md:col-span-4 lg:col-span-4 p-5 border-emerald-100 shadow-sm bg-white flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity">
                <i className="ri-robot-2-line text-[120px] -rotate-12"></i>
              </div>
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-base font-normal tracking-tight text-slate-900 flex items-center gap-2">
                    <i className="ri-robot-2-line text-purple-600"></i> AI Market Intelligence
                  </h4>
                  <Badge variant="success" className="animate-pulse">Live Analysis</Badge>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-1">
                  <div className="space-y-4">
                    <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
                      <p className="text-[10px] text-emerald-700 tracking-widest tracking-widest font-normal mb-1">Cơ hội mua hàng tốt nhất</p>
                      <p className="text-sm text-slate-800 font-normal mb-1">Cỏ nhung Nhật đang có xu hướng giảm giá 5% tại khu vực miền Nam.</p>
                      <button className="text-xs text-emerald-700 hover:underline">Xem danh sách NCC phù hợp</button>
                    </div>
                    <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                      <p className="text-[10px] text-amber-700 tracking-widest tracking-widest font-normal mb-1">Cảnh báo rủi ro đơn giá</p>
                      <p className="text-sm text-slate-800 font-normal mb-1">Giá Dầu Diesel dự báo tăng 3.5% vào tuần tới. Đề xuất chốt đơn sớm.</p>
                    </div>
                  </div>
                  
                  <div className="h-full min-h-[160px] bg-slate-50 border border-slate-100 rounded-xl relative overflow-hidden">
                    <svg className="absolute bottom-0 w-full h-[70%]" preserveAspectRatio="none" viewBox="0 0 100 100">
                      <path d="M0 80 Q 20 60, 40 70 T 80 30 T 100 20 L100 100 L0 100 Z" fill="#10b981" fillOpacity="0.1" />
                      <path d="M0 80 Q 20 60, 40 70 T 80 30 T 100 20" fill="none" stroke="#10b981" strokeWidth="2" />
                    </svg>
                    <div className="absolute inset-0 p-4">
                      <p className="text-[10px] text-slate-400 tracking-widest tracking-widest font-normal">Chỉ số giá toàn ngành</p>
                      <p className="text-2xl font-normal text-slate-900">104.2 <span className="text-xs text-emerald-600">+1.2%</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Bento 2: Quick Status (Vertical) */}
            <Card className="md:col-span-2 lg:col-span-2 p-5 border-slate-200 shadow-sm bg-slate-900 text-white flex flex-col justify-between overflow-hidden relative">
              <i className="ri-flashlight-line absolute -bottom-6 -right-6 text-[120px] text-white/5 rotate-12"></i>
              <div className="relative z-10">
                <h4 className="text-xs font-normal tracking-widest text-slate-400 tracking-widest mb-6">Trạng thái hệ thống</h4>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-xs text-slate-300">Độ phủ dữ liệu NCC</span>
                      <span className="text-sm font-normal">92%</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 w-[92%] shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-xs text-slate-300">Độ tin cậy đơn giá</span>
                      <span className="text-sm font-normal">85%</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 w-[85%] shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-xs text-slate-300">Dự án đang áp dụng</span>
                      <span className="text-sm font-normal">14</span>
                    </div>
                    <div className="flex -space-x-2 mt-2">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className="w-7 h-7 rounded-full border-2 border-slate-900 bg-slate-700 flex items-center justify-center text-[10px] text-slate-300">
                          {String.fromCharCode(64 + i)}
                        </div>
                      ))}
                      <div className="w-7 h-7 rounded-full border-2 border-slate-900 bg-emerald-600 flex items-center justify-center text-[10px] font-medium text-white">
                        +9
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button className="relative z-10 mt-8 w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-normal tracking-wide transition-colors border border-white/10">
                Xuất báo cáo Master Cost
              </button>
            </Card>

            {/* Bento 3: Supplier Performance (Medium) */}
            <Card className="md:col-span-3 lg:col-span-3 p-5 border-slate-200 shadow-sm bg-white">
              <h4 className="text-sm font-normal tracking-tight text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-truck-line text-blue-600"></i> Top Nhà cung cấp hiệu năng cao
              </h4>
              <div className="space-y-4">
                {MOCK_SUPPLIER.slice(0, 3).map(ncc => (
                  <div key={ncc.code} className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-pointer">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-200">
                      <i className="ri-building-line text-slate-500"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-normal text-slate-900 truncate">{ncc.name}</p>
                      <p className="text-xs text-slate-400">{ncc.group} • {ncc.area}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-normal text-amber-500"><i className="ri-star-fill"></i> {ncc.rating.split('/')[0]}</p>
                      <p className="text-[10px] text-slate-400 tracking-widest tracking-widest font-normal">Rating</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Bento 4: Recent Activities (Medium) */}
            <Card className="md:col-span-3 lg:col-span-3 p-5 border-slate-200 shadow-sm bg-white">
              <h4 className="text-sm font-normal tracking-tight text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-history-line text-emerald-600"></i> Lịch sử thay đổi giá gần đây
              </h4>
              <div className="space-y-4">
                {MOCK_HISTORY.slice(0, 3).map(hist => (
                  <div key={hist.code} className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-pointer">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                      <i className={hist.diff.startsWith('+') ? 'ri-arrow-up-line text-rose-500' : 'ri-arrow-down-line text-emerald-500'}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-normal text-slate-900 truncate">{hist.name}</p>
                      <p className="text-xs text-slate-400">{hist.reason} • {hist.date}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-normal ${hist.diff.startsWith('+') ? 'text-rose-600' : 'text-emerald-600'}`}>{hist.diff}</p>
                      <p className="text-[10px] text-slate-400 tracking-widest tracking-widest font-normal">Biến động</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* 2. MATERIALS TAB - 8/4 LAYOUT */}
        {/* 2. MATERIALS TAB - SPLIT VIEW (8/4) */}
        {activeTab === 'materials' && (
          <div className="flex h-full gap-4 overflow-hidden">
            {/* Main Table Area (8 parts) */}
            <div className="flex-[8] min-w-0 flex flex-col h-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-in fade-in duration-500">
              <TableActions />
              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/80 backdrop-blur sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="p-3 border-b border-slate-200 w-10 text-center">
                        <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" onChange={(e) => toggleAllRows(resources.map(m => m.code))} checked={selectedRows.length === resources.length && resources.length > 0} />
                      </th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal w-24">Mã</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Tên vật liệu</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Nhóm</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-16">ĐVT</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-right w-32">Đơn giá</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-20">Trend</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-20">Status</th>
                      <th className="p-3 border-b border-slate-200 w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                       <tr><td colSpan={9} className="p-8 text-center text-slate-400">Đang tải dữ liệu...</td></tr>
                    ) : resources.length === 0 ? (
                       <tr><td colSpan={9} className="p-8 text-center text-slate-400">Không có dữ liệu phù hợp</td></tr>
                    ) : (
                      resources.map((item, i) => (
                        <tr 
                          key={i} 
                          className={`hover:bg-blue-50/30 transition-all group cursor-pointer ${selectedItem?.id === item.id ? 'bg-blue-50/50' : ''}`}
                          onClick={() => setSelectedItem(item)}
                        >
                          <td className="p-3 text-center relative" onClick={(e) => e.stopPropagation()}>
                            {selectedItem?.id === item.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-600"></div>}
                            <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" checked={selectedRows.includes(item.code)} onChange={() => toggleRowSelection(item.code)} />
                          </td>
                          <td className="p-3 text-xs font-normal text-slate-500">{item.code}</td>
                          <td className="p-3">
                            <p className="text-sm font-normal text-slate-900 group-hover:text-emerald-700 transition-colors">{item.name}</p>
                            <p className="text-[10px] text-slate-400 font-normal truncate max-w-[200px]">{item.current_rate?.supplier?.name || 'Chưa cập nhật NCC'}</p>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded font-normal">{item.category?.name || 'N/A'}</span>
                          </td>
                          <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                          <td className="p-3 text-sm font-normal text-slate-800 text-right tabular-nums">
                            {item.current_rate?.price?.toLocaleString() || '0'}
                          </td>
                          <td className="p-3 flex justify-center"><MicroTrend up={i % 3 === 0} /></td>
                          <td className="p-3 text-center">
                            <Badge variant={getBadgeVariant(item.status)} className="scale-90">
                              {item.status}
                            </Badge>
                          </td>
                          <RowActions />
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Intelligence Sidebar (4 parts) */}
            <div className="flex-[4] min-w-[320px] flex flex-col h-full gap-4 sticky top-0 overflow-y-auto no-scrollbar">
              <IntelligenceSidebar item={selectedItem} />
            </div>
          </div>
        )}

        {activeTab === 'labor' && (
          <div className="flex h-full gap-4 overflow-hidden">
            {/* Main Table Area (8 parts) */}
            <div className="flex-[8] min-w-0 flex flex-col h-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-in fade-in duration-500">
              <TableActions />
              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead className="bg-slate-50/80 backdrop-blur sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal w-24">Mã</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Vị trí / Chuyên môn</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Nhóm</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-16">ĐVT</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-right w-32">Lương ngày</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-24">Kỹ năng</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-24">Status</th>
                      <th className="p-3 border-b border-slate-200 w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                       <tr><td colSpan={7} className="p-8 text-center text-slate-400">Đang tải...</td></tr>
                    ) : resources.map((item, i) => (
                      <tr 
                        key={i} 
                        className={`hover:bg-blue-50/30 transition-all group cursor-pointer ${selectedItem?.id === item.id ? 'bg-blue-50/50' : ''}`}
                        onClick={() => setSelectedItem(item)}
                      >
                        <td className="p-3 text-xs font-normal text-slate-500 relative">
                          {selectedItem?.id === item.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-600"></div>}
                          {item.code}
                        </td>
                        <td className="p-3">
                          <p className="text-sm font-normal text-slate-900 group-hover:text-emerald-700 transition-colors">{item.name}</p>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded font-normal">{item.category?.name || 'N/A'}</span>
                        </td>
                        <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                        <td className="p-3 text-sm font-normal text-indigo-700 text-right tabular-nums">
                          {item.current_rate?.price?.toLocaleString() || '0'}
                        </td>
                        <td className="p-3 text-center">
                           <span className="text-xs text-slate-600 font-normal">{item.specifications?.skill || 'Cơ bản'}</span>
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant={getBadgeVariant(item.status)} className="scale-90">
                            {item.status}
                          </Badge>
                        </td>
                        <RowActions />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Intelligence Sidebar (4 parts) */}
            <div className="flex-[4] min-w-[320px] flex flex-col h-full gap-4 sticky top-0 overflow-y-auto no-scrollbar">
              <IntelligenceSidebar item={selectedItem} />
            </div>
          </div>
        )}

        {/* 5. EQUIPMENT TAB */}
        {activeTab === 'equipment' && (
          <div className="flex h-full gap-4 overflow-hidden">
            {/* Main Table Area (8 parts) */}
            <div className="flex-[8] min-w-0 flex flex-col h-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-in fade-in duration-500">
              <TableActions />
              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead className="bg-slate-50/80 backdrop-blur sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal w-24">Mã</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Tên máy / Thiết bị</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal">Nhóm</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-center w-16">ĐVT</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-right w-32">Giá thuê ca</th>
                      <th className="p-3 border-b border-slate-200 text-[10px] tracking-widest tracking-widest text-slate-500 font-normal text-right w-32">Nhiên liệu</th>
                      <th className="p-3 border-b border-slate-200 w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                       <tr><td colSpan={7} className="p-8 text-center text-slate-400">Đang tải...</td></tr>
                    ) : resources.map((item, i) => (
                      <tr 
                        key={i} 
                        className={`hover:bg-blue-50/30 transition-all group cursor-pointer ${selectedItem?.id === item.id ? 'bg-blue-50/50' : ''}`}
                        onClick={() => setSelectedItem(item)}
                      >
                        <td className="p-3 text-xs font-normal text-slate-500 relative">
                          {selectedItem?.id === item.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-600"></div>}
                          {item.code}
                        </td>
                        <td className="p-3">
                          <p className="text-sm font-normal text-slate-900 group-hover:text-emerald-700 transition-colors">{item.name}</p>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded font-normal">{item.category?.name || 'N/A'}</span>
                        </td>
                        <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                        <td className="p-3 text-sm font-normal text-orange-700 text-right tabular-nums">
                          {item.current_rate?.price?.toLocaleString() || '0'}
                        </td>
                        <td className="p-3 text-sm font-normal text-slate-600 text-right tabular-nums">
                          {item.specifications?.fuel || 'N/A'}
                        </td>
                        <RowActions />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Intelligence Sidebar (4 parts) */}
            <div className="flex-[4] min-w-[320px] flex flex-col h-full gap-4 sticky top-0 overflow-y-auto no-scrollbar">
              <IntelligenceSidebar item={selectedItem} />
            </div>
          </div>
        )}

        {/* 6. FUEL TAB */}
        {activeTab === 'fuel' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Mã</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên nhiên liệu</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">ĐVT</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-900 text-right">Đơn giá</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Biến động</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Nguồn giá</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Ngày cập nhật</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Trạng thái</th>
                    <th className="p-3 border-b border-slate-200 w-28 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_FUEL.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-slate-900">{item.name}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                      <td className="p-3 text-sm font-medium text-slate-900 text-right">{item.price}</td>
                      <td className={`p-3 text-sm font-medium text-center ${getVarColor(item.var)}`}>{item.var}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.source}</td>
                      <td className="p-3 text-xs text-center text-slate-500">{item.date}</td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* 7. AGRO TAB */}
        {activeTab === 'agro' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[900px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Mã</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên vật tư</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Nhóm</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">ĐVT</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-900 text-right">Đơn giá</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Liều lượng chuẩn</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-emerald-700 text-right">Chi phí quy đổi</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Trạng thái</th>
                    <th className="p-3 border-b border-slate-200 w-28 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_AGRO.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-slate-900">{item.name}</td>
                      <td className="p-3 text-xs text-slate-600">{item.group}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                      <td className="p-3 text-sm font-medium text-slate-900 text-right">{item.price}</td>
                      <td className="p-3 text-xs text-center font-medium text-slate-600">{item.dose}</td>
                      <td className="p-3 text-sm font-medium text-emerald-700 text-right bg-emerald-50/30">{item.cost}</td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* 8. SUPPLIER TAB */}
        {activeTab === 'suppliers' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[900px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Mã NCC</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên nhà cung cấp</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Nhóm cung cấp</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Khu vực</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Đánh giá</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Lead time</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Công nợ</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Trạng thái</th>
                    <th className="p-3 border-b border-slate-200 w-28 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_SUPPLIER.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-blue-700">{item.name}</td>
                      <td className="p-3 text-xs text-slate-600">{item.group}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.area}</td>
                      <td className="p-3 text-sm font-medium text-amber-500 text-center"><i className="ri-star-fill" /> {item.rating}</td>
                      <td className="p-3 text-xs text-center font-medium text-slate-700">{item.lead}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.debt}</td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* 9. HISTORY TAB */}
        {activeTab === 'history' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <div className="p-3 border-b border-slate-100 bg-white flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
              <div className="w-full sm:w-72">
                <TopbarSearch value="" onChange={() => {}} placeholder="Tìm kiếm mã, hạng mục..." />
              </div>
              <div className="flex w-full sm:w-auto gap-2">
                <select className="h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-slate-600 focus:outline-none focus:border-emerald-500 shadow-sm">
                  <option>Tất cả Hạng mục</option>
                  <option>Cây xanh</option>
                  <option>Vật liệu</option>
                  <option>Nhân công</option>
                  <option>Máy móc</option>
                </select>
                <select className="h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-slate-600 focus:outline-none focus:border-emerald-500 shadow-sm">
                  <option>Thời gian: 30 ngày qua</option>
                  <option>Thời gian: 7 ngày qua</option>
                  <option>Thời gian: Hôm nay</option>
                </select>
                <button 
                  onClick={() => setHistoryView(historyView === 'table' ? 'timeline' : 'table')}
                  className="h-10 px-3 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-md flex items-center gap-2 justify-center transition-colors border border-slate-200 shadow-sm"
                >
                  <i className={historyView === 'table' ? 'ri-timeline-view' : 'ri-table-view'}></i>
                  {historyView === 'table' ? 'Timeline' : 'Bảng'}
                </button>
                <button className="h-10 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-md flex items-center justify-center transition-colors border border-slate-200 shadow-sm">
                  <i className="ri-filter-3-line"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-white">
              {historyView === 'table' ? (
                <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 w-24">Mã</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Hạng mục</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-500 text-right line-through">Giá cũ</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-900 text-right">Giá mới</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Chênh lệch</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Người cập nhật</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Lý do</th>
                      <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Ngày</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {MOCK_HISTORY.map((item, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                        <td className="p-3 text-sm font-medium text-slate-800">{item.name}</td>
                        <td className="p-3 text-sm text-slate-400 text-right line-through">{item.old}</td>
                        <td className="p-3 text-sm font-medium text-slate-900 text-right">{item.new}</td>
                        <td className={`p-3 text-sm font-medium text-center ${getVarColor(item.diff)}`}>{item.diff}</td>
                        <td className="p-3 text-xs text-center text-slate-600">
                          <span className="flex items-center justify-center gap-1"><div className="w-4 h-4 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center"><i className="ri-user-fill text-[10px] text-slate-500" /></div> {item.by}</span>
                        </td>
                        <td className="p-3 text-xs text-slate-500 italic">{item.reason}</td>
                        <td className="p-3 text-xs text-center text-slate-500 font-medium">{item.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="max-w-3xl mx-auto py-6">
                  <div className="relative border-l-2 border-slate-100 pl-6 ml-4 space-y-8">
                    {MOCK_HISTORY.map((item, i) => (
                      <div key={i} className="relative">
                        <div className={`absolute -left-[35px] top-1 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${item.diff.startsWith('+') ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                          <i className={`text-white text-[10px] ${item.diff.startsWith('+') ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`} />
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="text-xs font-medium text-slate-500">{item.code}</span>
                              <h4 className="font-medium text-slate-800">{item.name}</h4>
                            </div>
                            <span className="text-xs font-medium text-slate-400 bg-white px-2 py-1 rounded border border-slate-200">{item.date}</span>
                          </div>
                          <div className="flex items-center gap-4 my-3 bg-white p-3 rounded border border-slate-100">
                            <div className="text-slate-400 line-through text-sm font-medium">{item.old}</div>
                            <i className="ri-arrow-right-line text-slate-300"></i>
                            <div className="text-slate-900 font-medium">{item.new}</div>
                            <div className={`ml-auto font-medium text-sm px-2 py-1 rounded ${item.diff.startsWith('+') ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                              {item.diff}
                            </div>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200/60">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><i className="ri-user-smile-fill text-xs" /></div>
                              <span className="text-xs font-medium text-slate-600">{item.by}</span>
                            </div>
                            <span className="text-xs italic text-slate-500">"{item.reason}"</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        
        {/* CÔNG TÁC CHUẨN TAB */}
        {activeTab === 'standard_tasks' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 w-24">Mã</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên công tác</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">ĐVT</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Nhóm</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Thành phần cấu thành</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Thời gian định mức</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Trạng thái</th>
                    <th className="p-3 border-b border-slate-200 w-28 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_STANDARD_TASKS.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-emerald-800">{item.name}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.unit}</td>
                      <td className="p-3 text-xs text-slate-600">{item.group}</td>
                      <td className="p-3 text-xs text-slate-600 font-medium">{item.comp}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.time}</td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* BỘ ĐỊNH MỨC TAB */}
        {activeTab === 'norm_sets' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto p-4 bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {MOCK_NORM_SETS.map((item, i) => (
                  <Card key={i} className="p-4 bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all cursor-pointer">
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                        <i className="ri-book-read-line text-xl" />
                      </div>
                      <Badge variant={item.status === 'Active' ? 'success' : 'neutral'}>{item.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 font-medium mb-1">{item.code}</p>
                    <h4 className="font-medium text-slate-800 text-sm mb-3">{item.name}</h4>
                    <div className="space-y-2 text-xs text-slate-600">
                      <div className="flex justify-between border-b border-slate-50 pb-1"><span className="text-slate-400">Loại:</span> <span className="font-medium">{item.type}</span></div>
                      <div className="flex justify-between border-b border-slate-50 pb-1"><span className="text-slate-400">Khu vực áp dụng:</span> <span className="font-medium">{item.region}</span></div>
                      <div className="flex justify-between border-b border-slate-50 pb-1"><span className="text-slate-400">Số lượng items:</span> <span className="font-medium text-indigo-600">{item.items}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Cập nhật:</span> <span>{item.updated}</span></div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* HAO HỤT TAB */}
        {activeTab === 'waste' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Nhóm vật tư/công tác</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Quy tắc hao hụt</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Tỷ lệ (%)</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Cảnh báo hệ thống (AI Benchmark)</th>
                    <th className="p-3 border-b border-slate-200 w-28 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_WASTE_RULES.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-sm font-medium text-slate-800">{item.group}</td>
                      <td className="p-3 text-xs text-slate-600">{item.rule}</td>
                      <td className="p-3 text-sm font-medium text-rose-600 text-center bg-rose-50/30">{item.value}</td>
                      <td className="p-3 text-xs">
                        <span className={`flex items-center gap-1 ${item.alert.includes('Cao') ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                          {item.alert.includes('Cao') && <i className="ri-error-warning-fill" />}
                          {item.alert}
                        </span>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* CÔNG THỨC TAB */}
        {activeTab === 'formula' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto p-4 bg-slate-50">
              <div className="space-y-4 max-w-4xl mx-auto">
                {MOCK_FORMULAS.map((item, i) => (
                  <Card key={i} className="p-4 bg-white border border-slate-200 shadow-sm flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-[10px] font-medium rounded ${item.type === 'Hệ thống' ? 'bg-indigo-50 text-indigo-700' : 'bg-orange-50 text-orange-700'}`}>{item.type}</span>
                        <h4 className="font-medium text-slate-800">{item.name}</h4>
                      </div>
                      <div className="font-mono text-sm text-emerald-700 bg-emerald-50/50 p-3 rounded border border-emerald-100/50 break-all">
                        {item.formula}
                      </div>
                    </div>
                    <div className="pl-6 border-l border-slate-100 ml-6 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); toast.info('Đang mở trình sửa công thức'); }} className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-600 mb-2 transition-colors"><i className="ri-edit-line"></i></button>
                      <button onClick={(e) => { e.stopPropagation(); toast.success('Đã xóa công thức'); }} className="w-8 h-8 rounded-full hover:bg-slate-100 text-rose-600 transition-colors"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* GIÁ THAM CHIẾU TAB */}
        {activeTab === 'reference_cost' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 w-24">Mã VT</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên Tài Nguyên</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-right bg-emerald-50/50">Giá Master (Hiện hành)</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">NCC 1</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">NCC 2</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Lịch sử DA/Khu vực</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_REF_COSTS.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-slate-800">{item.name}</td>
                      <td className="p-3 text-sm font-medium text-emerald-700 text-right bg-emerald-50/30">{item.current}</td>
                      <td className="p-3 text-xs text-slate-600 text-center">{item.refA}</td>
                      <td className="p-3 text-xs text-slate-600 text-center">{item.refB}</td>
                      <td className="p-3 text-xs text-slate-500 text-center italic">{item.history}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* PHIÊN BẢN TAB */}
        {activeTab === 'revisions' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto p-8">
              <div className="max-w-3xl mx-auto space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                {MOCK_REVISIONS.map((item, i) => (
                  <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                      <div className={`w-3 h-3 rounded-full ${item.status === 'Current' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-slate-800 text-lg flex items-center gap-2">
                          {item.rev} 
                          {item.status === 'Current' && <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded border border-emerald-100 tracking-wide text-[10px]">Current</span>}
                        </div>
                        <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded">{item.date}</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{item.changes}</p>
                      <div className="flex items-center justify-between text-xs border-t border-slate-50 pt-2">
                        <span className="text-slate-500 flex items-center gap-1"><i className="ri-user-line"></i> {item.author}</span>
                        <button onClick={() => toast.info('Đang mở giao diện so sánh...')} className="text-blue-600 font-medium hover:underline">Compare</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* IMPORT/EXPORT TAB */}
        {activeTab === 'import_export' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <TableActions />
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card className="border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors cursor-pointer group">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm">
                    <i className="ri-upload-cloud-2-line text-3xl text-emerald-600"></i>
                  </div>
                  <h4 className="font-medium text-slate-800 text-lg mb-1">Import Excel</h4>
                  <p className="text-sm text-slate-500">Tải lên file định dạng mẫu chuẩn (.xlsx) để đồng bộ dữ liệu vào hệ thống</p>
                  <button onClick={() => setIsModalOpen(true)} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium shadow-sm hover:bg-emerald-700 transition-colors">
                    Chọn File Tải Lên
                  </button>
                </Card>
                <Card className="border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:border-blue-500 hover:bg-blue-50/50 transition-colors cursor-pointer group">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm">
                    <i className="ri-download-cloud-2-line text-3xl text-blue-600"></i>
                  </div>
                  <h4 className="font-medium text-slate-800 text-lg mb-1">Export Excel</h4>
                  <p className="text-sm text-slate-500">Trích xuất bảng giá và định mức hiện tại ra file Excel để lưu trữ hoặc chia sẻ</p>
                  <button onClick={() => toast.success('Đã xuất file excel thành công')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium shadow-sm hover:bg-blue-700 transition-colors">
                    Xuất File Excel
                  </button>
                </Card>
              </div>

              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2"><i className="ri-history-line"></i> Lịch sử Import/Export</h4>
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Mã File</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Tên File</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Phân loại</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Số dòng</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-emerald-600 text-center">Thành công</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-rose-600 text-center">Lỗi</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Thực hiện bởi</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_FILES.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-sm font-medium text-blue-600 hover:underline cursor-pointer flex items-center gap-2"><i className="ri-file-excel-2-fill text-emerald-600"></i> {item.name}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.type}</td>
                      <td className="p-3 text-xs font-medium text-slate-700 text-center bg-slate-50/50">{item.rows}</td>
                      <td className="p-3 text-xs font-medium text-emerald-600 text-center">{item.success}</td>
                      <td className="p-3 text-xs font-medium text-rose-600 text-center">{item.err > 0 ? item.err : '-'}</td>
                      <td className="p-3 text-xs text-center text-slate-600">{item.by}</td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}


        {/* 11. AI PRICING TAB */}
        {activeTab === 'ai' && (
          <Card className="h-full border-slate-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 bg-purple-50/30 flex justify-between items-center shrink-0">
              <div>
                <h3 className="font-medium text-lg text-purple-800 flex items-center gap-2">
                  <div className="relative">
                    <i className="ri-robot-2-fill text-purple-600 animate-pulse relative z-10" />
                    <div className="absolute inset-0 bg-purple-400 blur-md rounded-full opacity-50 animate-pulse"></div>
                  </div>
                  AI Pricing Engine
                </h3>
                <p className="text-xs text-purple-600/80 mt-1 font-medium">Phát hiện bất thường và đề xuất tối ưu hóa chi phí dự toán theo thời gian thực</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toast.success('Đã lưu cấu hình AI mới')} className="bg-white border border-purple-200 text-purple-700 px-4 py-2 rounded-lg sm:text-sm text-xs font-medium shadow-sm hover:bg-purple-50 transition-colors">
                  <i className="ri-settings-4-line" /> Cấu hình AI
                </button>
                <button onClick={() => toast.success('Đã quét xong hệ thống')} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg sm:text-sm text-xs font-medium shadow-sm transition-colors">
                  <i className="ri-refresh-line" /> Quét lại hệ thống
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 w-24">Mã cảnh báo</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 w-24 text-center">Nhóm</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700">Nội dung AI phát hiện</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center w-28">Mức độ</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-purple-800">Đề xuất của AI</th>
                    <th className="p-3 border-b border-slate-200 text-xs font-medium text-slate-700 text-center w-32">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_AI.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-xs font-medium text-slate-500">{item.code}</td>
                      <td className="p-3 text-xs text-center font-medium text-slate-700">{item.group}</td>
                      <td className="p-3 text-sm font-medium text-slate-800">{item.desc}</td>
                      <td className="p-3 text-center"><span className={`px-2 py-1 text-[10px] font-medium rounded border ${getLevelColor(item.level)}`}>{item.level}</span></td>
                      <td className="p-3">
                        <div className="flex items-center justify-between gap-3 bg-purple-50/50 p-2 rounded-lg border border-purple-100 group-hover:bg-white transition-colors">
                          <span className="text-sm font-medium text-purple-700 flex items-center gap-2"><i className="ri-lightbulb-flash-line text-amber-500" /> {item.suggest}</span>
                          <button onClick={(e) => { e.stopPropagation(); toast.success('Đã áp dụng đề xuất của AI'); }} className="px-3 py-1.5 bg-purple-100 hover:bg-purple-600 hover:text-white text-purple-700 text-xs font-medium rounded shadow-sm transition-colors opacity-80 group-hover:opacity-100 flex items-center gap-1 shrink-0">
                            <i className="ri-check-line" /> Áp dụng
                          </button>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={getBadgeVariant(item.status)} className="whitespace-nowrap">
                          {item.status}
                        </Badge>
                      </td>
                      <RowActions />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* AI Chat Input */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
              <div className="relative max-w-4xl mx-auto flex items-center">
                <div className="absolute left-3 flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 shadow-sm">
                  <i className="ri-magic-line"></i>
                </div>
                <input type="text" placeholder="Hỏi Trợ lý AI phân tích xu hướng giá của Vật tư bất kỳ (VD: Dự báo giá cỏ lá gừng tháng sau?)..." className="w-full pl-14 pr-12 py-3 bg-white border border-purple-100/50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all shadow-[0_2px_10px_-3px_rgba(147,51,234,0.1)]" />
                <button onClick={() => toast.info('AI đang xử lý yêu cầu...')} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm">
                  <i className="ri-send-plane-fill"></i>
                </button>
              </div>
            </div>
          </Card>
        )}

      </div>

      {/* Dynamic Modal for actions */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-medium text-slate-800 text-lg flex items-center gap-2">
                <><i className="ri-file-excel-2-fill text-emerald-600" /> Import Excel Hàng loạt</>
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:bg-rose-50 hover:text-rose-500 w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 flex flex-col items-center justify-center text-center hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors cursor-pointer group">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm">
                    <i className="ri-file-excel-2-line text-3xl text-emerald-600"></i>
                  </div>
                  <p className="font-medium text-slate-700 mb-1 text-lg">Kéo thả file Excel vào đây</p>
                  <p className="text-sm text-slate-500">Hoặc click để duyệt file (.xlsx, .csv)</p>
                  <div className="mt-6 px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 shadow-sm flex items-center gap-2">
                    <i className="ri-download-line text-blue-600" /> Tải file mẫu chuẩn
                  </div>
                </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors shadow-sm">Hủy bỏ</button>
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2 bg-emerald-600 rounded-lg text-sm font-medium text-white hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-2">
                <><i className="ri-upload-cloud-2-line" /> Bắt đầu Import</>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
